/**
 * ConnectorHealthPanel ,  admin widget showing live freshness of every
 * county connector. Traffic-light bucket counts on top, a sortable per-row
 * table below. Used at the top of DiscoveryConfigsAdmin.
 */
import { useEffect, useState } from "react";
import { Activity, RefreshCw, AlertTriangle, CheckCircle2, Clock, MinusCircle, PowerOff } from "lucide-react";
import api from "@/lib/api";

const BUCKET_META = {
  ok:       { label: "Healthy",  color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", icon: CheckCircle2 },
  stale:    { label: "Stale",    color: "text-amber-300 border-amber-500/40 bg-amber-500/10",       icon: Clock },
  failed:   { label: "Failed",   color: "text-rose-300 border-rose-500/40 bg-rose-500/10",          icon: AlertTriangle },
  never:    { label: "Never run",color: "text-slate-300 border-slate-500/40 bg-slate-500/10",       icon: MinusCircle },
  disabled: { label: "Disabled", color: "text-slate-500 border-slate-700/40 bg-white/2",            icon: PowerOff },
};

const CATEGORY_LABELS = {
  lis_pendens: "Lis Pendens",
  eviction: "Evictions",
  vacant_registry: "Vacant Reg.",
  code_violation: "Code Viol.",
  probate: "Probate",
  divorce: "Divorce",
};

function ageString(iso) {
  if (!iso) return ", ";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const h = ms / 3_600_000;
    if (h < 1) return `${Math.round(h * 60)}m ago`;
    if (h < 48) return `${Math.round(h)}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch { return ", "; }
}

export default function ConnectorHealthPanel({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/discovery/configs/health");
      setData(r.data);
      // Auto-expand when there are connectors needing attention so admins
      // don't miss them behind the "Show details" button.
      if (compact && r.data?.buckets) {
        const trouble = (r.data.buckets.failed || 0) + (r.data.buckets.stale || 0);
        if (trouble > 0) setExpanded(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="ae-card p-5 text-slate-500 text-sm" data-testid="connector-health-loading">
        Loading connector health…
      </div>
    );
  }
  if (!data) return null;

  const { total, buckets = {}, by_category = {}, rows = [] } = data;
  const enabledTotal = total - (buckets.disabled || 0);
  const healthPct = enabledTotal > 0
    ? Math.round(((buckets.ok || 0) / enabledTotal) * 100)
    : 0;

  return (
    <div className="ae-card p-5 space-y-4" data-testid="connector-health-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Live Connector Health
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-heading text-2xl tracking-tight text-slate-100" data-testid="health-pct">
              {healthPct}<span className="text-slate-500 text-sm">%</span>
            </div>
            <div className="text-xs text-slate-500">
              of {enabledTotal} enabled connectors healthy
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load} disabled={loading}
            data-testid="connector-health-refresh"
            className="btn-ghost text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {compact && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="btn-ghost text-[11px]"
              data-testid="connector-health-toggle"
            >
              {expanded ? "Hide details" : "Show details"}
            </button>
          )}
        </div>
      </div>

      {/* Bucket pills */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="connector-health-buckets">
        {Object.entries(BUCKET_META).map(([k, meta]) => {
          const Icon = meta.icon;
          const n = buckets[k] || 0;
          return (
            <div
              key={k}
              className={`px-3 py-2 border rounded-sm flex items-center justify-between ${meta.color}`}
              data-testid={`health-bucket-${k}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5" />
                <span className="text-[10px] uppercase tracking-wider">{meta.label}</span>
              </div>
              <span className="font-mono tabular-nums text-lg">{n}</span>
            </div>
          );
        })}
      </div>

      {expanded && (
        <>
          {/* Per-category mini bars */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="connector-health-categories">
            {Object.entries(by_category).map(([cat, c]) => (
              <CategoryStack key={cat} category={cat} counts={c} />
            ))}
          </div>

          {/* Problem rows table (failed + stale + never) */}
          <div data-testid="connector-health-rows">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Connectors needing attention
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                  <tr className="border-b border-white/10">
                    <th className="text-left pb-2 pr-2">Status</th>
                    <th className="text-left pb-2 pr-2">County</th>
                    <th className="text-left pb-2 pr-2">Category</th>
                    <th className="text-left pb-2 pr-2">Schedule</th>
                    <th className="text-left pb-2 pr-2">Last run</th>
                    <th className="text-left pb-2">Message</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {rows.filter((r) => r.bucket !== "ok" && r.bucket !== "disabled").slice(0, 12).map((r) => {
                    const meta = BUCKET_META[r.bucket] || BUCKET_META.never;
                    const Icon = meta.icon;
                    return (
                      <tr key={r.cid} className="border-b border-white/5" data-testid={`health-row-${r.cid}`}>
                        <td className="py-2 pr-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${meta.color}`}>
                            <Icon className="h-3 w-3" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2 pr-2 text-slate-100">{r.county}, {r.state}</td>
                        <td className="py-2 pr-2 text-slate-400">{CATEGORY_LABELS[r.category] || r.category}</td>
                        <td className="py-2 pr-2 font-mono text-[10px] uppercase text-slate-500">{r.schedule}</td>
                        <td className="py-2 pr-2 font-mono text-[10px] text-slate-500">{ageString(r.last_run)}</td>
                        <td className="py-2 text-slate-500 truncate max-w-xs">{r.last_message || ", "}</td>
                      </tr>
                    );
                  })}
                  {rows.filter((r) => r.bucket !== "ok" && r.bucket !== "disabled").length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-emerald-400 text-[11px]">
                        ✓ All enabled connectors are healthy.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function CategoryStack({ category, counts }) {
  const total = counts.total || 1;
  const seg = (k) => `${Math.max(2, ((counts[k] || 0) / total) * 100)}%`;
  return (
    <div data-testid={`health-cat-${category}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        <span>{CATEGORY_LABELS[category] || category}</span>
        <span className="font-mono">{counts.total}</span>
      </div>
      <div className="flex h-1.5 rounded-sm overflow-hidden bg-white/5">
        {counts.ok ? <div style={{ width: seg("ok") }} className="bg-emerald-500/80" title={`OK: ${counts.ok}`} /> : null}
        {counts.stale ? <div style={{ width: seg("stale") }} className="bg-amber-500/80" title={`Stale: ${counts.stale}`} /> : null}
        {counts.failed ? <div style={{ width: seg("failed") }} className="bg-rose-500/80" title={`Failed: ${counts.failed}`} /> : null}
        {counts.never ? <div style={{ width: seg("never") }} className="bg-slate-500/60" title={`Never: ${counts.never}`} /> : null}
        {counts.disabled ? <div style={{ width: seg("disabled") }} className="bg-slate-700/80" title={`Disabled: ${counts.disabled}`} /> : null}
      </div>
    </div>
  );
}
